// src/pages/OurScents.js
// Product listing page — fetches products from Shopify with localised pricing.
// Re-fetches whenever the user changes currency so prices update instantly.

import React, { useState, useEffect, useRef } from 'react';
import { getScents, BEST_SELLER_HANDLE, LOW_STOCK, NEW_RELEASE_HANDLES } from '../data/scents';
import { useCart, GIFT_BAG_HANDLE } from '../context/CartContext';
import { useCurrency } from '../context/CurrencyContext';
import { useWishlist } from '../context/WishlistContext';
import { ShoppingBag, Check, Search, Heart } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ProductSkeleton from '../components/ProductSkeleton';
import ProductRatingBadge from '../components/ProductRatingBadge';
import usePageTitle from '../hooks/usePageTitle';

// Capitalise first letter of each tag for display
const capitalise = (str) => str.charAt(0).toUpperCase() + str.slice(1);

const OurScents = () => {
    const { addToCart, cart, loading: cartLoading } = useCart();
    const navigate = useNavigate();

    usePageTitle(
        'Our Scents',
        'Shop our full collection of signature concentrated perfume oils. Bold, long-lasting fragrances designed to leave your mark.',
        'buy perfume oil, perfume oil fragrance, concentrated perfume, arabic perfume oil, long lasting perfume'
    );

    const { country, symbol } = useCurrency();
    const { isWishlisted, toggleWishlist } = useWishlist();

    const [addedItems, setAddedItems] = useState({});
    const [tooltipItems, setTooltipItems] = useState({});
    const [addPendingId, setAddPendingId] = useState(null);
    const cartBeforeRef = useRef(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('ALL');
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        setLoading(true);
        setError(null);
        getScents(country)
            .then(setProducts)
            .catch((err) => setError(err.message))
            .finally(() => setLoading(false));
    }, [country]);

    // Only show the "Added" confirmation once addToCart's mutation has
    // actually landed — detected by the cart object changing identity, since
    // CartContext only calls setCart on a successful add, never on failure.
    useEffect(() => {
        if (!addPendingId || cartLoading) return;
        const id = addPendingId;
        setAddPendingId(null);
        if (cart !== cartBeforeRef.current) {
            setAddedItems(prev => ({ ...prev, [id]: true }));
            setTimeout(() => {
                setAddedItems(prev => ({ ...prev, [id]: false }));
            }, 2000);
        }
    }, [addPendingId, cartLoading, cart]);

    const handleAddToCart = (e, product) => {
        e.stopPropagation();
        cartBeforeRef.current = cart;
        setAddPendingId(product.id);
        addToCart(product.variantId);
    };

    const handleWishlist = (e, product) => {
        e.stopPropagation();
        const saved = toggleWishlist(product.handle);
        if (!saved) {
            setTooltipItems(prev => ({ ...prev, [product.id]: true }));
            setTimeout(() => {
                setTooltipItems(prev => ({ ...prev, [product.id]: false }));
            }, 2000);
        }
    };

    // Build unique category list from product tags, sorted alphabetically
    const categories = [
        'ALL',
        ...Array.from(new Set(products.flatMap(p => p.tags || []))).sort()
    ];

    const filteredProducts = products.filter(product => {
        const matchesCategory = selectedCategory === 'ALL' ||
            (product.tags || []).map(t => t.toLowerCase()).includes(selectedCategory.toLowerCase());
        const matchesSearch = searchQuery === '' ||
            product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            product.description.toLowerCase().includes(searchQuery.toLowerCase());
        return matchesCategory && matchesSearch;
    });

    if (error) return (
        <div className="bg-background min-h-screen pt-32 flex items-center justify-center">
            <p className="text-red-400 text-sm">Failed to load products: {error}</p>
        </div>
    );

    return (
        <div className="bg-background min-h-screen pt-32 pb-24 px-4 sm:px-6 transition-colors duration-300">
            <div className="max-w-7xl mx-auto">

                <div className="text-center mb-12 sm:mb-16">
                    <h1 className="text-4xl sm:text-5xl md:text-6xl font-light text-foreground mb-4 tracking-wide">
                        Our Scents
                    </h1>
                    <p className="text-muted-foreground text-base sm:text-lg max-w-2xl mx-auto px-4">
                        Bold fragrance oils designed to leave your mark. Each scent commands attention
                        and lingers in memory because you're not here to blend in.
                    </p>
                </div>

                {/* Search bar */}
                <div className="max-w-md mx-auto mb-8">
                    <div className="relative">
                        <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-muted-foreground w-5 h-5" />
                        <input
                            type="text"
                            placeholder="Search fragrances..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-12 pr-4 py-3 rounded-full bg-card border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-[#CEAC8F] focus:ring-2 focus:ring-[#CEAC8F]/20 transition-all"
                        />
                        {searchQuery && (
                            <button
                                onClick={() => setSearchQuery('')}
                                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground text-sm"
                            >
                                Clear
                            </button>
                        )}
                    </div>
                </div>

                {/* Category filter pills */}
                {!loading && categories.length > 1 && (
                    <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-12 sm:mb-16">
                        {categories.map((category) => (
                            <button
                                key={category}
                                onClick={() => setSelectedCategory(category)}
                                className={`px-4 sm:px-6 py-2 rounded-full text-xs sm:text-sm tracking-wide transition-all ${
                                    selectedCategory === category
                                        ? 'bg-[#CEAC8F] text-black'
                                        : 'bg-card text-muted-foreground hover:text-foreground border border-border hover:border-[#CEAC8F]/30'
                                }`}
                            >
                                {category === 'ALL' ? 'All' : capitalise(category)}
                            </button>
                        ))}
                    </div>
                )}

                {/* Products grid */}
                <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-6 md:gap-8">
                    {loading
                        ? Array.from({ length: 6 }).map((_, i) => <ProductSkeleton key={i} />)
                        : filteredProducts.map((product) => (
                            <div
                                key={product.id}
                                onClick={() => navigate(`/product/${product.handle}`)}
                                className="group relative bg-card rounded-xl sm:rounded-2xl overflow-hidden transition-all duration-500 cursor-pointer"
                            >
                                {(NEW_RELEASE_HANDLES.includes(product.handle) || product.handle === BEST_SELLER_HANDLE || LOW_STOCK[product.handle]) && (
                                    <div className="absolute top-3 left-3 z-10 flex flex-col gap-1.5">
                                        {NEW_RELEASE_HANDLES.includes(product.handle) && (
                                            <span className="bg-[#4E2326] text-white text-[10px] font-medium px-2.5 py-1 rounded-full uppercase tracking-widest">
                                                New
                                            </span>
                                        )}
                                        {product.handle === BEST_SELLER_HANDLE && (
                                            <span className="bg-[#CEAC8F] text-black text-[10px] font-medium px-2.5 py-1 rounded-full uppercase tracking-widest">
                                                Best Seller
                                            </span>
                                        )}
                                        {LOW_STOCK[product.handle] && (
                                            <span className="bg-red-600 text-white text-[10px] font-medium px-2.5 py-1 rounded-full uppercase tracking-widest">
                                                Low Stock
                                            </span>
                                        )}
                                    </div>
                                )}
                                <div className="aspect-[3/4] flex items-center justify-center relative overflow-hidden">
                                    <img
                                        src={product.image}
                                        alt={product.imageAlt}
                                        loading="lazy"
                                        className="w-full h-full object-contain rounded-lg sm:rounded-2xl group-hover:scale-105 transition-transform duration-700"
                                    />

                                    <div className="absolute top-3 right-3">
                                        {tooltipItems[product.id] && (
                                            <div className="absolute -top-8 right-0 bg-background text-foreground text-xs px-2 py-1 rounded-lg border border-border whitespace-nowrap shadow-lg">
                                                Sign in to save
                                            </div>
                                        )}
                                        <button
                                            onClick={(e) => handleWishlist(e, product)}
                                            className="w-8 h-8 bg-background/80 backdrop-blur-sm rounded-full flex items-center justify-center transition-all hover:scale-110"
                                            aria-label="Save to wishlist"
                                        >
                                            <Heart
                                                className={`w-4 h-4 transition-colors ${
                                                    isWishlisted(product.handle)
                                                        ? 'fill-[#CEAC8F] text-[#CEAC8F]'
                                                        : 'text-muted-foreground'
                                                }`}
                                            />
                                        </button>
                                    </div>
                                </div>

                                <div className="p-3 sm:p-6">
                                    {product.tags?.length > 0 && (
                                        <p className="text-[#CEAC8F]/70 text-[10px] sm:text-xs tracking-widest mb-1 sm:mb-2 uppercase">
                                            {product.tags[0]}
                                        </p>
                                    )}
                                    <h3 className="text-foreground text-sm sm:text-xl font-light mb-1 group-hover:text-[#CEAC8F] transition-colors">
                                        {product.name}
                                    </h3>
                                    <ProductRatingBadge productId={product.id} productTitle={product.name} />
                                    {product.handle !== GIFT_BAG_HANDLE && (
                                        <p className="text-muted-foreground/70 text-[10px] sm:text-xs tracking-widest mb-1 sm:mb-2 uppercase">
                                            12ml
                                        </p>
                                    )}
                                    <p className="text-muted-foreground text-xs sm:text-sm mb-3 sm:mb-4 line-clamp-2">
                                        {product.description}
                                    </p>

                                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-0 mt-3 sm:mt-4">
                                        <p className="text-[#CEAC8F] text-lg sm:text-2xl font-light">
                                            {symbol}{product.price.toFixed(2)}
                                        </p>

                                        <button
                                            onClick={(e) => handleAddToCart(e, product)}
                                            disabled={!product.availableForSale || cartLoading}
                                            className={`w-full sm:w-auto flex items-center justify-center gap-2 px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all ${
                                                addedItems[product.id]
                                                    ? 'bg-green-600 text-white'
                                                    : product.availableForSale
                                                        ? 'bg-[#CEAC8F] text-black hover:bg-[#d4b896]'
                                                        : 'bg-muted text-muted-foreground cursor-not-allowed'
                                            }`}
                                        >
                                            {addedItems[product.id] ? (
                                                <><Check className="w-3 h-3 sm:w-4 sm:h-4" />Added</>
                                            ) : product.availableForSale ? (
                                                <><ShoppingBag className="w-3 h-3 sm:w-4 sm:h-4" />Add</>
                                            ) : (
                                                'Sold Out'
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))
                    }
                </div>

                {!loading && filteredProducts.length === 0 && (
                    <div className="text-center py-16">
                        <p className="text-muted-foreground text-lg mb-2">No fragrances found.</p>
                        <p className="text-muted-foreground/60 text-sm">Try adjusting your search or filter.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default OurScents;